import { useRef, useMemo, useCallback, useEffect } from 'react';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const scrollToElementOnMobile = _wg('scrollToElementOnMobile');

interface Column {
  key: string;
  label: string;
  field?: string;
  linkField?: boolean;
  fullWidth?: boolean;
}

interface NPSDSearchResultsProps {
  columns: Column[];
  results?: any[];
  loading?: boolean;
  hasSearched?: boolean;
  totalRecords?: number;
  currentPage?: number;
  pageSize?: number;
  rowDisabled?: ((item: any) => boolean) | null;
  showNewTabButton?: boolean;
  cellRenderers?: Record<string, (item: any, value: string) => React.ReactNode>;
  renderCardTitle?: (item: any) => React.ReactNode;
  onRowClick?: (item: any) => void;
  onLinkClick?: (item: any) => void;
  onOpenNewTab?: (item: any) => void;
  onCurrentPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

const NEW_TAB_SVG = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
    <path d="M320 0c-17.7 0-32 14.3-32 32s14.3 32 32 32h82.7L201.4 265.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L448 109.3V192c0 17.7 14.3 32 32 32s32-14.3 32-32V32c0-17.7-14.3-32-32-32H320zM80 32C35.8 32 0 67.8 0 112V432c0 44.2 35.8 80 80 80H400c44.2 0 80-35.8 80-80V320c0-17.7-14.3-32-32-32s-32 14.3-32 32V432c0 8.8-7.2 16-16 16H80c-8.8 0-16-7.2-16-16V112c0-8.8 7.2-16 16-16H192c17.7 0 32-14.3 32-32s-14.3-32-32-32H80z" />
  </svg>
);

export function NPSDSearchResults({
  columns,
  results = [],
  loading = false,
  hasSearched = false,
  totalRecords = 0,
  currentPage = 1,
  pageSize = 25,
  rowDisabled = null,
  showNewTabButton = false,
  cellRenderers,
  renderCardTitle,
  onRowClick,
  onLinkClick,
  onOpenNewTab,
  onCurrentPageChange,
  onPageSizeChange,
}: NPSDSearchResultsProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const prevLoadingRef = useRef(loading);

  const totalPages = useMemo(() => {
    return Math.ceil(totalRecords / pageSize) || 1;
  }, [totalRecords, pageSize]);

  const startRecord = useMemo(() => {
    if (totalRecords === 0) return 0;
    return ((currentPage - 1) * pageSize) + 1;
  }, [currentPage, pageSize, totalRecords]);

  const endRecord = useMemo(() => {
    return Math.min(currentPage * pageSize, totalRecords);
  }, [currentPage, pageSize, totalRecords]);

  const visiblePages = useMemo(() => {
    const pages: number[] = [];
    const total = totalPages;
    const current = currentPage;
    let start = Math.max(1, current - 2);
    let end = Math.min(total, start + 4);
    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }, [totalPages, currentPage]);

  const getCellValue = useCallback((item: any, column: Column): string => {
    if (column.field) {
      const value = item[column.field];
      return value != null ? value : '\u2014';
    }
    return '\u2014';
  }, []);

  const isRowDisabled = useCallback((item: any): boolean => {
    if (rowDisabled) {
      return rowDisabled(item);
    }
    return false;
  }, [rowDisabled]);

  const handleRowClick = useCallback((item: any) => {
    if (!isRowDisabled(item)) {
      onRowClick?.(item);
    }
  }, [isRowDisabled, onRowClick]);

  const handleLinkClick = useCallback((item: any) => {
    if (!isRowDisabled(item)) {
      onLinkClick?.(item);
    }
  }, [isRowDisabled, onLinkClick]);

  const handleNewTabClick = useCallback((item: any) => {
    if (!isRowDisabled(item)) {
      onOpenNewTab?.(item);
    }
  }, [isRowDisabled, onOpenNewTab]);

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages) {
      onCurrentPageChange?.(page);
    }
  }, [totalPages, onCurrentPageChange]);

  const handlePageSizeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    onPageSizeChange?.(parseInt(event.target.value, 10));
    onCurrentPageChange?.(1);
  }, [onPageSizeChange, onCurrentPageChange]);

  const scrollToResultsOnMobile = useCallback(() => {
    if (rootRef.current) {
      scrollToElementOnMobile(rootRef.current, 60, 768);
    }
  }, []);

  // Watch loading: when loading finishes (true -> false) on page 1 with results, scroll on mobile
  useEffect(() => {
    if (prevLoadingRef.current === true && loading === false && hasSearched && results.length > 0 && currentPage === 1) {
      scrollToResultsOnMobile();
    }
    prevLoadingRef.current = loading;
  }, [loading, hasSearched, results.length, currentPage, scrollToResultsOnMobile]);

  const renderCellContent = useCallback((item: any, col: Column) => {
    if (cellRenderers && cellRenderers[col.key]) {
      return cellRenderers[col.key](item, getCellValue(item, col));
    }
    return getCellValue(item, col);
  }, [cellRenderers, getCellValue]);

  return (
    <div className="npsd-search-results-component" ref={rootRef}>
      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div>Searching...</div>
        </div>
      )}

      {/* Empty State */}
      {!loading && hasSearched && results.length === 0 && (
        <div className="empty-state">
          No results found.
        </div>
      )}

      {/* Desktop Table */}
      {!loading && hasSearched && results.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
                {showNewTabButton && <th className="new-tab-col-header"></th>}
              </tr>
            </thead>
            <tbody>
              {results.map((item, index) => (
                <tr
                  key={item.id || index}
                  onClick={() => handleRowClick(item)}
                  className={isRowDisabled(item) ? 'row-disabled' : undefined}
                >
                  {columns.map((col, colIndex) => (
                    <td key={col.key}>
                      {colIndex === 0 && col.linkField !== false ? (
                        !isRowDisabled(item) ? (
                          <a
                            role="button"
                            className="cell-link"
                            onClick={(e) => { e.stopPropagation(); handleLinkClick(item); }}
                          >
                            {renderCellContent(item, col)}
                          </a>
                        ) : (
                          <span>{renderCellContent(item, col)}</span>
                        )
                      ) : (
                        renderCellContent(item, col)
                      )}
                    </td>
                  ))}
                  {showNewTabButton && (
                    <td className="new-tab-col">
                      {!isRowDisabled(item) && (
                        <a
                          role="button"
                          className="new-tab-icon"
                          title="Open in New Tab"
                          onClick={(e) => { e.stopPropagation(); handleNewTabClick(item); }}
                        >
                          {NEW_TAB_SVG}
                        </a>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile Cards */}
      {!loading && hasSearched && results.length > 0 && (
        <div className="mobile-cards">
          {results.map((item, index) => (
            <div
              className={`result-card${isRowDisabled(item) ? ' card-disabled' : ''}`}
              key={'card-' + (item.id || index)}
              onClick={() => handleRowClick(item)}
            >
              <div className="card-header">
                <span className="card-title">
                  {renderCardTitle ? renderCardTitle(item) : getCellValue(item, columns[0])}
                </span>
                {showNewTabButton && !isRowDisabled(item) && (
                  <a
                    role="button"
                    className="new-tab-icon"
                    title="Open in New Tab"
                    onClick={(e) => { e.stopPropagation(); handleNewTabClick(item); }}
                  >
                    {NEW_TAB_SVG}
                  </a>
                )}
              </div>
              <div className="card-body">
                {columns.map((col) => (
                  <div
                    key={'card-' + col.key}
                    className={`card-row${col.fullWidth ? ' full-width' : ''}`}
                  >
                    <span className="card-label">{col.label}</span>
                    <span className="card-value">
                      {renderCellContent(item, col)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination — only when there are results (matches KstDataGrid's hasRows gate;
          totalPages has a `|| 1` fallback so it can't be used to detect emptiness) */}
      {!loading && hasSearched && results.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {startRecord} - {endRecord} of {totalRecords} results
          </div>
          <div className="pagination-controls">
            <button className="pagination-btn" disabled={currentPage === 1} onClick={() => goToPage(1)}>First</button>
            <button className="pagination-btn" disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)}>Prev</button>
            {visiblePages.map((p) => (
              <button
                key={p}
                className={`pagination-btn${p === currentPage ? ' active' : ''}`}
                onClick={() => goToPage(p)}
              >{p}</button>
            ))}
            <button className="pagination-btn" disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)}>Next</button>
            <button className="pagination-btn" disabled={currentPage === totalPages} onClick={() => goToPage(totalPages)}>Last</button>
            <select className="page-size-select" value={pageSize} onChange={handlePageSizeChange}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

(window as any).NPSDSearchResultsReact = NPSDSearchResults;
